import { DefaultButton, DetailsList, Dialog, DialogFooter, IColumn, Icon, IconButton, Label, PrimaryButton, Selection, SelectionMode, Stack, TextField, TooltipHost } from "office-ui-fabric-react";
import * as React from "react";
import { approveBooking, rejectBooking } from "../../../api/bookings";
import { getRoomInfo } from "../../../api/rooms";
import { ProfileContext } from "../../../Home";
import { BookingStatus, IBooking, IMyBooking } from "../../../types/Booking.types";
import { IRoom, RoomType } from "../../../types/SiteLocation.types";

import styles from "./MyBookings.module.scss";
import { useHistory } from "react-router";

export interface IBookingListProps {
    isApprovalMode?: boolean;
    bookings?: IMyBooking[] | IBooking[];
    label: string;
    listKey: string;
    selection: Selection;
    showStatus?: boolean;
    tooltip?: string;
    onChange?: (reload?: boolean, error?: Error) => void;
}

export const BookingList = (props: IBookingListProps) => {
    const { bookings, label, listKey, selection, tooltip } = props;

    const userContext = React.useContext(ProfileContext);
    const history = useHistory();

    const [ reason, setReason ] = React.useState<string|undefined>();
    const [ showReasonDialog, setShowReasonDialog ] = React.useState<number|undefined>();

    // Cancellation Callback
    const _onRejectClick = React.useCallback((bookingId: number) => {
        return async () => {
            let cancel = false, cancelledByAdmin = false;
            const booking = bookings?.find((b) => b.Id === bookingId);
            let isOwner = false;
            if (userContext && booking && (
                booking.BookedByUserId === userContext.Profile.Id ||
                (booking.OnBehalfOfEmail && userContext.Profile.Email && booking.OnBehalfOfEmail.toLowerCase() === userContext.Profile.Email.toLowerCase())
            )) {
                isOwner = true;
            }
            if (booking) {
                const roomInfoResponse = await getRoomInfo(booking.RoomId as number);
                if (roomInfoResponse.ok) {
                    let roomInfo: IRoom = await roomInfoResponse.json();
                    if (!isOwner && roomInfo?.IsRoomAdmin) {
                        if (!showReasonDialog && !Boolean(reason)) {
                            setShowReasonDialog(bookingId);
                        } else {
                            cancel = true;
                            cancelledByAdmin = true;
                        }
                    } else {
                        cancel = window.confirm('Are you sure?');
                    }
                    if (cancel) {
                        const response = await rejectBooking(bookingId, {CancellationReason: reason, CancelledByAdmin: cancelledByAdmin });
                        if (props.onChange) {
                            if (response === true) {
                                props.onChange(true);
                            } else {
                                props.onChange(false, response);
                            }
                        }
                    }
                }
            } else {
                if (props.onChange) props.onChange(false, new Error("Unable to reject booking - issue with booking data"));
            }
        };
    }, [ bookings, props, reason, showReasonDialog, userContext ]);

    const _approveBooking = React.useCallback(async (bookingId: number) => {
        if (window.confirm(`Approve this booking? Are you sure?`)) {
            let response = await approveBooking(bookingId);
            if (props.onChange) {
                if (response instanceof Error) {
                    props.onChange(false, response);
                } else {
                    props.onChange(true);
                }
            } 
        }
    }, [props]);


    const columns = React.useMemo(() => {
        const fmh = new Date();
        const fma = new Date();
        fma.setMinutes(fmh.getMinutes() - 15);
        fmh.setMinutes(fmh.getMinutes() + 15);
        const rtn = [
            {
                key: "deskName",
                name: props.isApprovalMode ? "Room" : "Desk / Room",
                fieldName: "DeskName",
                minWidth: 80,
                maxWidth: 100,
                onRender: (item: IMyBooking, index, column) => {
                    // For other orgs or if sites expand may want to list location and floorplan here too 
                    const isDesk = Boolean(item.DeskId);
                    let resourceName = isDesk ? item.DeskName : item.RoomName;
                    if (!resourceName || resourceName.trim().length === 0) resourceName = `${isDesk ? `Desk` : `Room`} (Id: ${isDesk ? item.DeskId : item.RoomId}`;
                    return (
                        <TooltipHost content={item.RoomType === RoomType.MeetingRoom ? "Meeting Room" : "Clinical Room"}>
                            {resourceName}
                            <div style={{fontSize: 10}}>
                                {item.SiteName}
                            </div>
                        </TooltipHost>
                    )
                }
            },
            {
                key: "onBehalf",
                name: "On Behalf Of",
                fieldName: "OnBehalfOfEmail",
                minWidth: 80,
                maxWidth: 100,
                onRender: (item: IMyBooking) => {
                    return item.OnBehalfOfEmail ? item.OnBehalfOfEmail : "Self";
                },
            },
            {
                key: "start",
                name: "Start",
                fieldName: "Start",
                minWidth: 80,
                maxWidth: 150,
                onRender: (item: IMyBooking, index, column) => {
                    if (item.Start) {
                        const date = new Date(item.Start);
                        let dateStr = date.toLocaleDateString();
                        if (window.innerWidth < 768) dateStr = dateStr.substr(0, 5);
                        return  dateStr + " " + date.toLocaleTimeString().substr(0,5);
                    }
                }
            },
            {
                key: "finish",
                name: "Finish",
                fieldName: "Finish",
                minWidth: 80,
                maxWidth: 150,
                onRender: (item: IMyBooking, index, column) => {
                    if (item.Finish) {
                        const date = new Date(item.Finish);
                        let dateStr = date.toLocaleDateString();
                        if (window.innerWidth < 768) dateStr = dateStr.substr(0, 5);
                        return  dateStr + " " + date.toLocaleTimeString().substr(0,5);
                    }
                }
            },
        ] as IColumn[];
        if (!props.isApprovalMode) {
            rtn.push({
                key: "parking",
                name: "Parking",
                fieldName: "IncludeParking",
                iconName: "ParkingSolid",
                isIconOnly: true,
                minWidth: 40,
                maxWidth: 40,
                onRender: (item: IMyBooking, index, column) => {
                    if (item.IncludeParking) {
                        return <TooltipHost content="Parking Booked">
                            <Icon iconName="ParkingSolid" style={{fontSize: 24}} />
                        </TooltipHost>;
                    }
                }
            },
            {
                key: "directions",
                name: "Directions",
                fieldName: "PostCode",
                iconName: "MapDirections",
                isIconOnly: true,
                minWidth: 40,
                maxWidth: 40,
                onRender: (item: IMyBooking, index, column) => {
                    if (item.PostCode) {
                        return <TooltipHost content="Get Directions">
                            <IconButton iconProps={{iconName: "MapDirections"}} 
                                style={{fontSize: 24}} 
                                href={`https://www.google.com/maps/dir/?api=1&destination=${ item.PostCode }`}
                                target="_blank"
                            />
                        </TooltipHost>
                    }
                }
            });
        } else {
            rtn.push({
                key: "description",
                name: "Description",
                fieldName: "Description",
                minWidth: 150,
                maxWidth: 200
            },
            {
                key: "approve",
                name: "Approve / Reject",
                fieldName: "Id",
                minWidth: 150,
                maxWidth: 200,
                onRender: (item: IMyBooking, index, column) => {
                    return (
                        <Stack horizontal verticalAlign="start" tokens={{childrenGap: 10}}>
                            <PrimaryButton text="Approve" styles={{ label: { whiteSpace: "nowrap" } }} onClick={() => _approveBooking(item.Id)} />
                            <DefaultButton text="Reject" styles={{ label: { whiteSpace: "nowrap" } }} onClick={_onRejectClick(item.Id)} />
                        </Stack>
                    )
                }
            });
        }
        if (props.bookings?.some((b) => {
            const dt = new Date(b.Start);
            return b.Status === 1 && dt > fma && dt < fmh;
        })) {
            rtn.push({
                key: "check-in",
                name: "Check In",
                fieldName: "ID",
                minWidth: 100,
                onRender: (item: IMyBooking, index, column) => {
                    const itemDt = new Date(item.Start);
                    if (!(item.Status === 1 && itemDt > fma && itemDt < fmh)) return null;
                    return (
                        <PrimaryButton text="Check In" onClick={() => {
                            history.push(`/check-in/${item.SiteId}/${item.LocationId}/${item.RoomId}`);
                        }} />
                    );
                }
            });
        }
        if (props.showStatus) {
            rtn.push({
                key: "status",
                name: "Status",
                fieldName: "Status",
                minWidth: 80,
                onRender: (item: IMyBooking, index, column) => {
                    let status: string | JSX.Element | null = null;
                    switch (item.Status) {
                        case 0: {
                            status = "Pending";
                            break;
                        }
                        case 1: {
                            status = "Approved";
                            break;
                        }
                        case 2: {
                            status = "Rejected";
                            break;
                        }
                        case 3: {
                            status = "Cancelled";
                            break;
                        }
                        case 4: {
                            status = "Auto-Cancelled (No Check-In)";
                        }
                    } 
                    if (item.Status as number > 1) {
                        let reason = "";
                        if (Boolean(item.CancellationReason)) reason += `${item.CancellationReason} `;
                        if (Boolean(item.CancelledByUserName)) reason += `(${item.Status === BookingStatus.Rejected ? 'Rejected' : 'Cancelled'} by: ${item.CancelledByUserName})`;
                        status = <TooltipHost content={reason.trim()}>
                            { status } {Boolean(reason.trim()) && <Icon iconName="Info" />}
                        </TooltipHost>
                    }
                    return status;
                }
            })
        }
        return rtn;
    }, [_approveBooking, _onRejectClick, props]);

    if (!bookings || !bookings.length) return null;

    let labelJsx = <Label styles={{root: { marginTop: 20 }}}>{label}</Label>;
    if (tooltip) labelJsx = <TooltipHost content={tooltip}>{labelJsx}</TooltipHost>;

    return (
        <>
            {labelJsx}
            <div className={styles.bookingTable}>
                <DetailsList
                    setKey={listKey}
                    getKey={(item: IMyBooking) => `${listKey}_${item.Id}`}
                    items={bookings}
                    columns={columns}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                />
            </div>
            { showReasonDialog !== undefined && (
                <Dialog 
                    hidden={false}
                    dialogContentProps={{ showCloseButton: true, title: "Reject Booking Request", subText: "Please provide a reason for rejecting this booking request:" }} 
                    onDismiss={() => {
                        setReason(undefined);
                        setShowReasonDialog(undefined);
                    }}>
                    <TextField autoFocus multiline label="Rejection Reason" placeholder="Please enter a cancellation reason" value={reason} onChange={(e, newVal) => setReason(newVal)} />
                    <DialogFooter >
                        <Stack horizontal horizontalAlign="end" verticalAlign="center" tokens={{childrenGap: 10}}>
                            <PrimaryButton disabled={!Boolean(reason)} text="Reject Request" onClick={_onRejectClick(showReasonDialog)} />
                            <DefaultButton text="Cancel" onClick={() => {
                                setReason(undefined);
                                setShowReasonDialog(undefined);
                            }} />
                        </Stack>
                    </DialogFooter>
                </Dialog>
            )}
        </>
    );
};